import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
  Alert } from '@mui/material';
import { makeStyles } from '@mui/styles';
import SaveButton from 'components/common/save-button';
import DatePicker from 'components/date-picker/date-picker';
import NumberField from 'components/number-field';
import { OutlinedButton } from 'components/OutlinedButton';
import SelectField from 'components/select-field';
import TextField from 'components/text-field';
import { TextButton } from 'components/TextButton';
import React from 'react';
import { Control, Controller, UseFormReturn } from 'react-hook-form';
import { TFunction } from 'i18next';
import { DisabledFields } from 'types/disabledFields';
import { listingItemtype } from 'types/ledger';
import { AccountingType } from 'v2-types/order';

import { EntryLedgerType } from './services/types';

const useStyles = makeStyles((theme: any) => ({
  container: { padding: theme.spacing(3) },
  text: {
    textTransform: 'none',
    color: theme.palette.primary.main,
    fontWeight: 500,
  },
  icon: {
    fontSize: '16px',
    marginRight: theme.spacing(1.5),
    color: theme.palette.primary.main,
  },
  itempaddinType: {
    '& > * ': { marginTop: theme.spacing(2) },
    padding: theme.spacing(3),
    margin: 0,
  },
  itempaddinfoother: { '& > * ': { marginLeft: theme.spacing(2) } },
  errorIconContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  nocontendPadding: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  removecontentPadding: { '& .MuiDialogContent-root': { padding: 0 } },
  form: {
    [theme.breakpoints.down('md')]: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
    },
  },
  content: {
    [theme.breakpoints.down('md')]: {
      flex: '1 1 auto',
      minHeight: 0,
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch',
    },
  },
  dialogActions: {
    flex: '0 0 auto',
  },
  numeric: {
    '& input': {
      textAlign: 'right',
      fontVariantNumeric: 'tabular-nums',
    },
  },
}));

type AddReceiptProps = {
  open: boolean;
  onClose: () => void;
  handleSubmit: (e?: React.BaseSyntheticEvent<object, any, any> | undefined) => Promise<void>;
  control: Control<EntryLedgerType, object>;
  allowedCodes: listingItemtype[];
  allowedKinds: listingItemtype[];
  allowedLetters: listingItemtype[];
  errors: string[];
  prevData: AccountingType | null;
  t: TFunction;
  loadingEditEntry: boolean;
  loadingAddNewEntry: boolean;
  handleResetValue: () => void;
  methods: UseFormReturn<EntryLedgerType, object>;
  disableFields?: DisabledFields<EntryLedgerType>;
  hideFields?: DisabledFields<EntryLedgerType>;
};

const AddEntryScene = ({
  open,
  onClose,
  handleSubmit,
  control,
  allowedCodes,
  allowedKinds,
  allowedLetters,
  errors,
  prevData,
  t,
  loadingEditEntry,
  loadingAddNewEntry,
  handleResetValue,
  methods,
  disableFields,
  hideFields,
}: AddReceiptProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const prevDataisManual =
    prevData?.kind?.includes('disbursement') ||
    prevData?.kind?.includes('manual') ||
    prevData?.kind?.includes('receipt');
  const showLetter = !hideFields?.letter;
  const showNumber = !hideFields?.number;
  const letterNumberSize = showLetter && showNumber ? 6 : 12;
  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      fullWidth
      fullScreen={isMobile}
      className={classes.removecontentPadding}
    >
      <form onSubmit={handleSubmit} className={classes.form}>
        <DialogContent className={`${classes.nocontendPadding} ${classes.content}`}>
          <Grid container className={classes.itempaddinType} direction="column">
            <Grid item xs={12}>
              <Typography variant="h6" style={{ color: 'black' }}>
                {prevData?._id ? t('accounting:edit-ledger-entry') : t('accounting:add-new-ledger-entry')}
              </Typography>
            </Grid>
            {!!errors.length && (
              <Grid item xs={12}>
                <Alert severity="error" classes={{ icon: classes.errorIconContainer }}>
                  {errors.map((error) => (
                    <Typography variant="subtitle1">{error}</Typography>
                  ))}
                </Alert>
              </Grid>
            )}
            {!hideFields?.amount_override && (
              <Controller
                name="amount_override"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <NumberField
                    className={classes.numeric}
                    customRef={ref}
                    decimalScale={null}
                    handleBlur={field.onBlur}
                    value={field.value}
                    label={t('accounting:amount-override')}
                    disabled={!!disableFields?.amount_override}
                    onValueChange={({ floatValue }) => field.onChange(floatValue)}
                    thousandSeparator
                    prefix="$"
                  />
                )}
              />
            )}

            {!hideFields?.amount && (
              <Controller
                name="amount"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <NumberField
                    className={classes.numeric}
                    customRef={ref}
                    handleBlur={field.onBlur}
                    value={field.value}
                    label={t('accounting:amount')}
                    disabled={!!disableFields?.amount}
                    onValueChange={({ floatValue }) => field.onChange(floatValue)}
                    thousandSeparator
                    prefix="$"
                  />
                )}
              />
            )}

            {(showLetter || showNumber) && (
              <Grid container direction="row" spacing={2}>
                {showLetter && (
                  <Grid item xs={letterNumberSize}>
                    <Controller
                      name="letter"
                      control={control}
                      render={({ field: { ref, onChange, ...field } }) => (
                        <SelectField
                          {...field}
                          inputRef={ref}
                          value={allowedLetters.length > 0 ? field.value! : ''}
                          data={allowedLetters}
                          dataKey="code"
                          dataValue="code"
                          dataText="description"
                          label={t('accounting:letter')}
                          handleChange={onChange}
                          error={!!methods.formState.errors.letter}
                          disabled={!!disableFields?.letter}
                        />
                      )}
                    />
                  </Grid>
                )}
                {showNumber && (
                  <Grid item xs={letterNumberSize}>
                    <Controller
                      name="number"
                      control={control}
                      render={({ field: { ref, ...field } }) => (
                        <NumberField
                          className={classes.numeric}
                          {...field}
                          customRef={ref}
                          label={t('accounting:number')}
                          decimalScale={0}
                          isAllowed={({ floatValue = 0 }) => floatValue <= 99999}
                          error={!!methods.formState.errors.number}
                          disabled={!!disableFields?.number}
                        />
                      )}
                    />
                  </Grid>
                )}
              </Grid>
            )}

            {!hideFields?.description && (
              <Controller
                name="description"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <TextField
                    {...field}
                    inputRef={ref}
                    label={t('accounting:description')}
                    error={!!methods.formState.errors.description}
                    disabled={!!disableFields?.description}
                  />
                )}
              />
            )}

            {!hideFields?.code && (
              <Controller
                name="code"
                control={control}
                render={({ field: { ref, onChange, ...field } }) => (
                  <SelectField
                    {...field}
                    inputRef={ref}
                    value={allowedCodes.length > 0 ? field.value : ''}
                    data={allowedCodes}
                    dataKey="code"
                    dataValue="code"
                    dataText="description"
                    label={t('accounting:code')}
                    handleChange={onChange}
                    error={!!methods.formState.errors.description}
                    disabled={!!disableFields?.code}
                  />
                )}
              />
            )}
            {!hideFields?.kind && (
              <Controller
                name="kind"
                control={control}
                render={({ field: { ref, onChange, ...field } }) => (
                  <SelectField
                    {...field}
                    inputRef={ref}
                    value={allowedKinds.length > 0 ? field.value : ''}
                    data={allowedKinds}
                    dataKey="code"
                    dataValue="code"
                    dataText="description"
                    label={t('accounting:kind')}
                    handleChange={onChange}
                    error={!!methods.formState.errors.description}
                    disabled={!!disableFields?.kind}
                  />
                )}
              />
            )}

            {!hideFields?.entryDate && (
              <Controller
                name="entryDate"
                control={control}
                render={({ field: { ref, value, ...field } }) => (
                  <DatePicker
                    {...field}
                    inputRef={ref}
                    disableToolbar={false}
                    label={t('accounting:entry-date')}
                    value={value as string | Date}
                    disabled={!!disableFields?.entryDate}
                  />
                )}
              />
            )}

            {!hideFields?.annuality && (
              <Controller
                name="annuality"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <NumberField
                    className={classes.numeric}
                    {...field}
                    customRef={ref}
                    label={t('accounting:annuality')}
                    disabled={!!disableFields?.annuality}
                  />
                )}
              />
            )}

            {!hideFields?.months && (
              <Controller
                name="months"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <NumberField
                    className={classes.numeric}
                    {...field}
                    customRef={ref}
                    label={t('common:months-per-year')}
                    decimalScale={0}
                    error={!!methods.formState.errors.months}
                    disabled={!!disableFields?.months}
                  />
                )}
              />
            )}

            {!hideFields?.months_advanced && (
              <Controller
                name="months_advanced"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <NumberField
                    className={classes.numeric}
                    {...field}
                    customRef={ref}
                    label={t('common:paid-in-advance')}
                    decimalScale={0}
                    error={!!methods.formState.errors.months_advanced}
                    disabled={!!disableFields?.months_advanced}
                  />
                )}
              />
            )}
          </Grid>
        </DialogContent>
        <DialogActions
          disableSpacing
          style={{ padding: 16, justifyContent: 'space-between' }}
          className={`${classes.itempaddinfoother} ${classes.dialogActions}`}
        >
          {prevData?._id && !prevDataisManual ? (
            <TextButton
              text={t('accounting:reset', { defaultValue: 'Reset' })}
              icon="undo"
              buttonColor="inherit"
              onClick={handleResetValue}
              disabled={loadingAddNewEntry || loadingEditEntry}
            />
          ) : <Box />}

          <Box style={{ display: 'flex', gap: 8 }}>
            <OutlinedButton
              text={t('common:close')}
              icon="times"
              buttonColor="inherit"
              onClick={onClose}
              disabled={loadingAddNewEntry || loadingEditEntry}
            />
            <SaveButton
              type="submit"
              customText={t('common:save')}
              loading={loadingAddNewEntry || loadingEditEntry}
              disabled={!methods.formState.isValid}
            />
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddEntryScene;
